import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Copy, Download, Link2, Loader2, QrCode } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, buttonClass, inputClass } from "../components/Bits";

const SIZES = ["small", "medium", "large"];
const ZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles",
               "America/Phoenix", "America/Anchorage", "Pacific/Honolulu", "UTC"];
const STATUS_LABEL = { booked: "booked", cancelled: "cancelled · time held",
                       released: "time released" };
const STATUS_TONE = { booked: "gold", cancelled: "terra", released: "neutral" };
const DAYS = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"],
              ["6", "Sat"], ["7", "Sun"]];

const when = (stamp) => new Date(stamp).toLocaleString(undefined, {
  weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});

// Clipboard access is denied in some embedded and headless contexts, and an unhandled
// rejection there throws up the dev error overlay. Fail with a message instead.
const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copying is blocked here — select the text and copy it manually");
  }
};

const TypeCard = ({ meeting, onSave }) => {
  const [qr, setQr] = useState(null);
  const [size, setSize] = useState("medium");
  const [busy, setBusy] = useState(false);

  const showQr = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.get(
        `/crm/scheduling/types/${meeting.slug}/qr`,
        { params: { fmt: "png", size }, responseType: "blob" });
      setQr(URL.createObjectURL(data));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt) => {
    try {
      const { data } = await adminApi.get(
        `/crm/scheduling/types/${meeting.slug}/qr`,
        { params: { fmt, size }, responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = `ohc-${meeting.slug}-${size}.${fmt}`;
      link.click();
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <li className="space-y-4 py-5" data-testid={`meeting-type-${meeting.slug}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-cream/90">{meeting.name}</p>
          <p className="mt-1 text-xs leading-relaxed text-cream/45">{meeting.blurb}</p>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone={meeting.active ? "gold" : "neutral"}>
            {meeting.active ? `${meeting.minutes} min` : "paused"}
          </Pill>
          <button type="button" data-testid={`meeting-toggle-${meeting.slug}`}
                  onClick={() => onSave(meeting.slug, { active: !meeting.active })}
                  className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-gold">
            {meeting.active ? "Pause" : "Open"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <code className="rounded border border-cream/10 bg-cream/5 px-3 py-1.5 text-[11px] text-cream/60"
              data-testid={`meeting-url-${meeting.slug}`}>
          {meeting.url}
        </code>
        <button type="button" data-testid={`meeting-copy-${meeting.slug}`}
                onClick={() => copyText(meeting.url, "Link")}
                className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-3 py-1.5 text-[11px] text-cream/65 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
          <Copy className="h-3 w-3" /> Copy link
        </button>
        <a href={meeting.url} target="_blank" rel="noreferrer"
           data-testid={`meeting-open-${meeting.slug}`}
           className="inline-flex items-center gap-2 text-[11px] text-cream/45 transition-colors duration-200 hover:text-gold">
          <Link2 className="h-3 w-3" /> Preview page
        </a>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25">
          {meeting.booked} booked
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={size} onChange={(e) => setSize(e.target.value)}
                data-testid={`qr-size-${meeting.slug}`}
                className={`${inputClass} w-32`}>
          {SIZES.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <button type="button" onClick={showQr} disabled={busy}
                data-testid={`qr-show-${meeting.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs text-gold transition-colors duration-200 hover:bg-gold/10">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <QrCode className="h-3.5 w-3.5" />}
          Show QR
        </button>
        <button type="button" onClick={() => download("png")} data-testid={`qr-png-${meeting.slug}`}
                className="inline-flex items-center gap-2 text-xs text-cream/50 transition-colors duration-200 hover:text-cream">
          <Download className="h-3.5 w-3.5" /> PNG
        </button>
        <button type="button" onClick={() => download("svg")} data-testid={`qr-svg-${meeting.slug}`}
                className="inline-flex items-center gap-2 text-xs text-cream/50 transition-colors duration-200 hover:text-cream">
          <Download className="h-3.5 w-3.5" /> SVG
        </button>
      </div>

      {qr ? (
        <img src={qr} alt={`QR code for ${meeting.name}`} data-testid={`qr-image-${meeting.slug}`}
             className="h-40 w-40 rounded-lg border border-cream/10 bg-white p-2" />
      ) : null}
    </li>
  );
};

export default function AdminScheduling() {
  const [data, setData] = useState(null);
  const [waiting, setWaiting] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/crm/scheduling/overview").then(({ data: payload }) => setData(payload))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // The sign-in happens in its own tab, so watch for the connection landing here.
  useEffect(() => {
    if (!waiting) return undefined;
    const started = Date.now();
    const timer = setInterval(async () => {
      try {
        const { data: payload } = await adminApi.get("/crm/scheduling/overview");
        setData(payload);
        if (payload.calendar.connected) {
          setWaiting(false);
          toast.success("Outlook calendar connected");
        }
      } catch {
        // keep waiting — the other tab is mid sign-in
      }
      if (Date.now() - started > 300000) setWaiting(false);
    }, 3000);
    return () => clearInterval(timer);
  }, [waiting]);

  useEffect(() => {
    const state = new URLSearchParams(window.location.search).get("calendar");
    if (state === "connected") toast.success("Outlook calendar connected");
    if (state === "failed" || state === "expired") toast.error("Outlook did not connect — try again");
  }, []);

  const saveType = async (slug, changes) => {
    try {
      await adminApi.patch(`/crm/scheduling/types/${slug}`, changes);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveAvailability = async (changes) => {
    try {
      await adminApi.patch("/crm/scheduling/availability", changes);
      toast.success("Availability saved");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cancelBooking = async (id) => {
    try {
      await adminApi.post(`/crm/scheduling/bookings/${id}/cancel`);
      toast.success("Cancelled — they've been emailed, and the time stays held");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const releaseBooking = async (id) => {
    try {
      const { data: payload } = await adminApi.post(`/crm/scheduling/bookings/${id}/release`);
      toast.success(`${payload.freed.length} time${payload.freed.length === 1 ? "" : "s"} back on offer`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const connect = async () => {
    try {
      const { data: payload } = await adminApi.get("/crm/scheduling/outlook/start");
      // Microsoft sends X-Frame-Options: DENY, so sign-in has to leave the preview iframe.
      // No noopener here: window.open returns null with it, and the handle is how a
      // blocked pop-up is told apart from a real one.
      const tab = window.open(payload.url, "_blank");
      if (!tab) {
        toast.error("Allow pop-ups for this site, then press Connect Outlook again");
        return;
      }
      setWaiting(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) {
    return (
      <div className="flex items-center gap-3 text-sm text-cream/40" data-testid="scheduling-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the diary…
      </div>
    );
  }

  const { availability: rules, calendar } = data;

  return (
    <div className="space-y-7" data-testid="admin-scheduling-page">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">Scheduling</p>
        <h1 className="mt-3 font-serif text-4xl font-light leading-none text-cream">
          Let them pick the time
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cream/50">
          One public page per meeting type, each with its own link and QR code for your signature,
          flyers and one-pagers. Slots come from your weekly hours minus anything already booked —
          and minus whatever Outlook says you are busy with, once the calendar is connected.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[["Upcoming", data.upcoming.length, "booking-upcoming-stat"],
          ["Booked all time", data.bookings.length, "booking-total-stat"],
          ["Calendar", calendar.connected ? "Outlook connected"
            : calendar.issue ? "Needs attention" : "Not connected",
           "booking-calendar-stat"]].map(([label, value, testid]) => (
          <div key={label} className="rounded-xl border border-cream/10 bg-[#10211A] px-5 py-5"
               data-testid={testid}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/45">{label}</p>
            <p className="mt-3 font-serif text-2xl leading-none text-cream">{value}</p>
          </div>
        ))}
      </div>

      <Panel title="Outlook calendar" testid="panel-outlook">
        {calendar.connected ? (
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm text-cream/60" data-testid="outlook-connected">
              Connected. New bookings land in your Outlook calendar with the invite sent to the
              prospect, and your busy time is blocked out on the booking pages.
            </p>
            <button type="button" data-testid="outlook-disconnect"
                    onClick={() => adminApi.post("/crm/scheduling/outlook/disconnect")
                      .then(() => { toast.success("Disconnected"); load(); })}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-terra">
              Disconnect
            </button>
          </div>
        ) : calendar.issue ? (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-terra" data-testid="outlook-issue">
              {calendar.issue}
            </p>
            <p className="text-xs leading-relaxed text-cream/45">
              Bookings still work and land in the desk — only the Outlook sync is waiting.
            </p>
          </div>
        ) : calendar.configured ? (
          <div className="space-y-3">
            <p className="text-sm leading-relaxed text-cream/60">
              Sign in once with your Microsoft 365 account. The desk only reads your free/busy and
              writes the meetings you accept — nothing else.
            </p>
            <button type="button" onClick={connect} className={buttonClass}
                    data-testid="outlook-connect">
              <CalendarCheck className="h-3.5 w-3.5" /> Connect Outlook
            </button>
            {waiting ? (
              <p className="flex items-center gap-2 text-xs text-cream/45"
                 data-testid="outlook-waiting">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Finish the Microsoft sign-in in the tab that just opened — this page picks it up on
                its own.
              </p>
            ) : (
              <p className="text-xs leading-relaxed text-cream/35">
                Microsoft blocks its sign-in page from loading inside the preview frame, so it opens
                in a new tab.
              </p>
            )}

            <div className="rounded-lg border border-cream/10 bg-cream/5 p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                Reply URL for your app registration
              </p>
              <p className="mt-2 text-xs leading-relaxed text-cream/50">
                Entra needs this exact address under Authentication → Add a platform → Web, or
                sign-in stops with <span className="text-terra">AADSTS500113: no reply address is
                registered</span>.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <code className="rounded border border-cream/10 bg-[#0B1710] px-3 py-1.5 text-[11px] text-cream/70"
                      data-testid="outlook-redirect-uri">
                  {calendar.redirect_uri}
                </code>
                <button type="button" data-testid="outlook-copy-redirect"
                        onClick={() => copyText(calendar.redirect_uri, "Reply URL")}
                        className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-3 py-1.5 text-[11px] text-cream/65 transition-colors duration-200 hover:border-gold/50 hover:text-cream">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-cream/55" data-testid="outlook-not-configured">
            Bookings work now and land in the desk. To put them in your Outlook calendar too, your
            Microsoft 365 app registration details are needed — tenant ID, client ID and a client
            secret from the Entra admin centre.
          </p>
        )}
      </Panel>

      <Panel title="Booking links and QR codes" testid="panel-booking-links">
        <ul className="divide-y divide-cream/10">
          {data.types.map((meeting) => (
            <TypeCard key={meeting.slug} meeting={meeting} onSave={saveType} />
          ))}
        </ul>
      </Panel>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title="Your weekly hours" testid="panel-availability">
          <div className="flex flex-wrap gap-2" data-testid="availability-days">
            {DAYS.map(([value, label]) => {
              const on = rules.days.includes(Number(value));
              return (
                <button key={value} type="button" data-testid={`availability-day-${value}`}
                        onClick={() => saveAvailability({
                          days: on ? rules.days.filter((d) => d !== Number(value))
                            : [...rules.days, Number(value)].sort(),
                        })}
                        className={`rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-200 ${
                          on ? "border-gold/50 bg-gold/10 text-gold"
                            : "border-cream/15 text-cream/35 hover:text-cream"}`}>
                  {label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[["start_hour", `Day starts (${rules.timezone})`],
              ["end_hour", `Day ends (${rules.timezone})`],
              ["slot_minutes", "Slot length"], ["lead_hours", "Notice needed (hours)"]]
              .map(([key, label]) => (
              <label key={key} className="space-y-1.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                  {label}
                </span>
                <input type="number" defaultValue={rules[key]} className={inputClass}
                       data-testid={`availability-${key}`}
                       onBlur={(e) => {
                         const next = Number(e.target.value);
                         if (next !== rules[key]) saveAvailability({ [key]: next });
                       }} />
              </label>
            ))}
            <label className="space-y-1.5 sm:col-span-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                Your timezone
              </span>
              <select value={rules.timezone} className={inputClass}
                      data-testid="availability-timezone"
                      onChange={(e) => saveAvailability({ timezone: e.target.value })}>
                {ZONES.map((zone) => (
                  <option key={zone} value={zone}>{zone.replace("America/", "")}</option>
                ))}
              </select>
            </label>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-cream/35"
             data-testid="availability-note">
            Hours are your own wall clock — 9 means 9am where you are, through daylight saving
            either way. Prospects always see the times converted to their own timezone.
          </p>
        </Panel>

        <Panel title="Booked" testid="panel-bookings">
          <ul className="divide-y divide-cream/10">
            {data.bookings.slice().reverse().map((booking) => (
              <li key={booking.id} className="py-3" data-testid={`booking-${booking.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="text-sm text-cream/85">
                    {booking.name}
                    {booking.company ? (
                      <span className="text-cream/45"> · {booking.company}</span>
                    ) : null}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/70">
                    {when(booking.starts_at)}
                  </p>
                </div>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/30">
                  {booking.type_name} · {booking.email}
                </p>
                {booking.notes ? (
                  <p className="mt-1 text-xs leading-relaxed text-cream/45">{booking.notes}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <Pill tone={STATUS_TONE[booking.status] || "neutral"}>
                    {STATUS_LABEL[booking.status] || booking.status}
                  </Pill>
                  {booking.reschedule_count ? (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/30">
                      moved {booking.reschedule_count}×
                    </span>
                  ) : null}
                  {booking.status === "booked" ? (
                    <button type="button" data-testid={`booking-cancel-${booking.id}`}
                            onClick={() => cancelBooking(booking.id)}
                            className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-terra">
                      Cancel
                    </button>
                  ) : null}
                  {booking.status === "cancelled" ? (
                    <button type="button" data-testid={`booking-release-${booking.id}`}
                            onClick={() => releaseBooking(booking.id)}
                            className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35 transition-colors duration-200 hover:text-gold">
                      Release the time
                    </button>
                  ) : null}
                </div>
                {booking.cancel_reason ? (
                  <p className="mt-1 text-xs leading-relaxed text-terra/70">
                    Their reason: {booking.cancel_reason}
                  </p>
                ) : null}
              </li>
            ))}
            {data.bookings.length === 0 ? (
              <li className="py-3 text-sm text-cream/40" data-testid="bookings-empty">
                Nothing booked yet. Put the QR on your one-pager and the link in your signature.
              </li>
            ) : null}
          </ul>
          {data.held?.length ? (
            <p className="mt-4 text-xs leading-relaxed text-cream/40" data-testid="held-note">
              {data.held.length} cancelled {data.held.length === 1 ? "time is" : "times are"} still
              held and will not be offered to anyone else until you release {data.held.length === 1
                ? "it" : "them"}.
            </p>
          ) : null}
        </Panel>
      </div>
    </div>
  );
}
